class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector("form");
    facetForm.addEventListener("input", this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector("#FacetsWrapperDesktop");
    if (facetWrapper) facetWrapper.addEventListener("keyup", onKeyUpEscape);

    // Initialize view switcher
    this.initViewSwitcher();

    // Apply default filters on first load
    this.applyDefaultFilters();
  }

  initViewSwitcher() {
    // Listen for view switcher changes
    document.addEventListener("change", (event) => {
      if (event.target.name === "sort_by_view") {
        this.updateGridView(event.target.value);
        // Store the preference
        localStorage.setItem("grid_view_preference", event.target.value);
      }
    });

    // Apply saved preference or default on load
    this.applyGridViewPreference();
  }

  updateGridView(viewType) {
    const productGrid = document.getElementById("product-grid");
    if (!productGrid) return;

    // Remove existing grid classes
    productGrid.classList.remove("grid-cols-1", "grid-cols-2");

    if (viewType === "single-column") {
      productGrid.classList.add("grid-cols-1");
    } else {
      productGrid.classList.add("grid-cols-2");
    }
  }

  applyGridViewPreference() {
    const savedPreference =
      localStorage.getItem("grid_view_preference") || "dual-column";

    // Update the radio button state
    const radioButton = document.querySelector(
      `input[name="sort_by_view"][value="${savedPreference}"]`
    );
    if (radioButton) {
      radioButton.checked = true;
    }

    // Apply the grid class
    this.updateGridView(savedPreference);
  }

  applyDefaultFilters() {
    if (window.location.pathname.startsWith("/search")) {
      return;
    }

    // Only apply default filters if there are no URL params (first load without filters)
    const urlParams = new URLSearchParams(window.location.search);
    const hasExistingFilters = Array.from(urlParams.keys()).some(
      (key) => key.startsWith("filter.") || key === "sort_by"
    );

    // If filters already exist in URL, don't apply defaults
    if (hasExistingFilters) {
      return;
    }

    // Get default filters from data attribute
    const defaultFilters = this.dataset.defaultFilters;

    // Parse the JSON array string (e.g., ["Diamond","Pearl"])
    let filterValues = [];

    // If no default filters are set, use Diamond and Pearl as defaults
    if (!defaultFilters || defaultFilters === "[]" || defaultFilters === "") {
      filterValues = ["diamond", "pearl"];
    } else {
      try {
        // Split on "," to parse the JSON-like array string
        const items = defaultFilters.split('","');
        filterValues = items.map((item) =>
          item.replace(/^\["?/, "").replace(/"?\]$/, "").trim().toLowerCase()
        );
      } catch (error) {
        console.error("Error parsing default filters:", error);
        return;
      }
    }

    if (filterValues.length === 0) {
      return;
    }

    // Find and check all matching checkboxes
    const forms = this.querySelectorAll("form");
    let filtersApplied = false;

    forms.forEach((form) => {
      const checkboxes = form.querySelectorAll(
        'input[type="checkbox"][data-filter-value]'
      );

      checkboxes.forEach((checkbox) => {
        const filterValue = checkbox.dataset.filterValue || "";

        // Extract material name from format "material: yellow gold"
        // The value.value in Shopify contains the full filter value like "material: yellow gold"
        let materialName = "";
        if (filterValue.includes(":")) {
          // Split on ":" and take the part after it
          materialName = filterValue
            .split(":")
            .slice(1)
            .join(":")
            .trim()
            .toLowerCase();
        } else {
          materialName = filterValue.toLowerCase().trim();
        }

        // Check if this checkbox's material matches any of the default filters
        if (filterValues.includes(materialName)) {
          checkbox.checked = true;
          filtersApplied = true;
        }
      });
    });

    // If filters were applied, trigger form submission to apply them
    if (filtersApplied) {
      const form = this.querySelector("form");
      if (form) {
        const searchParams = this.createSearchParams(form);
        // Use setTimeout to ensure DOM is ready
        setTimeout(() => {
          FacetFiltersForm.renderPage(searchParams, null);
        }, 100);
      }
    }
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state
        ? event.state.searchParams
        : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener("popstate", onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll(".js-facet-remove").forEach((element) => {
      element.classList.toggle("disabled", disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    const parsedParams = searchParams
      ? Array.from(new URLSearchParams(searchParams).entries())
      : [];
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById("ProductCount");
    const countContainerDesktop = document.getElementById(
      "ProductCountDesktop"
    );
    const loadingSpinners = document.querySelectorAll(
      ".facets-container .loading__spinner, facet-filters-form .loading__spinner"
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove("hidden"));
    document
      .getElementById("ProductGridContainer")
      .querySelector(".collection")
      .classList.add("loading");
    if (countContainer) {
      countContainer.classList.add("loading");
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add("loading");
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [
          ...FacetFiltersForm.filterData,
          { html, url },
        ];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        if (typeof initializeScrollAnimationTrigger === "function")
          initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    if (typeof initializeScrollAnimationTrigger === "function")
      initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    // Store infinite scroll state before updating DOM
    const currentContainer = document.getElementById("ProductGridContainer");
    const infiniteScrollData =
      FacetFiltersForm.preserveInfiniteScrollData(currentContainer);

    // First extract carousel data before updating DOM
    FacetFiltersForm.extractCarouselData(html);

    const newHTML = new DOMParser().parseFromString(html, "text/html");
    const newContainer = newHTML.getElementById("ProductGridContainer");

    if (!newContainer) {
      console.warn(
        "[facets] renderProductGridContainer: no ProductGridContainer found in response"
      );
      return;
    }

    const productItems = Array.from(
      newContainer.querySelectorAll("#product-grid .grid__item")
    );
    const productDebug = productItems.map((item) => {
      const link = item.querySelector('a[href*="/products/"]');
      return {
        title: link?.textContent?.trim(),
        href: link?.getAttribute("href"),
      };
    });

    const totalResultsText = newHTML
      .querySelector("#ProductCount")
      ?.textContent?.trim();

    const newInfiniteScrollEnabled =
      newContainer.dataset.infiniteScroll === "false";

    currentContainer.dataset.infiniteScroll =
      newContainer.dataset.infiniteScroll ??
      currentContainer.dataset.infiniteScroll;
    currentContainer.dataset.currentPage =
      newContainer.dataset.currentPage || "1";
    currentContainer.dataset.totalPages =
      newContainer.dataset.totalPages || "1";
    currentContainer.dataset.productsPerPage =
      newContainer.dataset.productsPerPage ||
      infiniteScrollData.productsPerPage ||
      "16";
    currentContainer.dataset.collectionUrl =
      newContainer.dataset.collectionUrl ||
      infiniteScrollData.collectionUrl ||
      window.location.pathname;

    currentContainer.innerHTML = newContainer.innerHTML;

    document
      .getElementById("ProductGridContainer")
      .querySelectorAll(".scroll-trigger")
      .forEach((element) => {
        element.classList.add("scroll-trigger--cancel");
      });

    // Re-apply grid view preference after content is updated
    FacetFiltersForm.reapplyGridViewPreference();

    // Remount carousels after DOM update
    FacetFiltersForm.remountCarousels();

    // Reinitialize infinite scroll if it was enabled
    FacetFiltersForm.reinitializeInfiniteScroll({
      ...infiniteScrollData,
      isEnabled: newInfiniteScrollEnabled,
      currentPage: currentContainer.dataset.currentPage,
      totalPages: currentContainer.dataset.totalPages,
      productsPerPage: currentContainer.dataset.productsPerPage,
      collectionUrl: currentContainer.dataset.collectionUrl,
    });
  }

  static preserveInfiniteScrollData(container) {
    if (!container) return { isEnabled: false };

    return {
      isEnabled: container.dataset.infiniteScroll === "false",
      currentPage: container.dataset.currentPage || "1",
      totalPages: container.dataset.totalPages || "1",
      productsPerPage: container.dataset.productsPerPage || "16",
      collectionUrl:
        container.dataset.collectionUrl || window.location.pathname,
    };
  }

  static reinitializeInfiniteScroll(infiniteScrollData) {
    if (!infiniteScrollData.isEnabled) return;

    // Wait for DOM to be fully updated
    setTimeout(() => {
      const container = document.querySelector(
        '[data-infinite-scroll="false"]'
      );
      if (container && window.InfiniteScroll) {
        // Destroy existing instance if any
        if (window.currentInfiniteScrollInstance) {
          window.currentInfiniteScrollInstance.destroy?.();
        }

        // Create new instance
        window.currentInfiniteScrollInstance = new window.InfiniteScroll(
          container
        );
      }
    }, 100);
  }

  static extractCarouselData(html) {
    const parsedHTML = new DOMParser().parseFromString(html, "text/html");
    const carouselScripts = parsedHTML.querySelectorAll("script:not([src])");

    carouselScripts.forEach((script) => {
      const scriptContent = script.textContent || script.innerText;
      if (scriptContent.includes("window.carouselData")) {
        try {
          // Create a new script element and execute it
          const newScript = document.createElement("script");
          newScript.textContent = scriptContent;
          document.head.appendChild(newScript);
          document.head.removeChild(newScript);
        } catch (error) {
          console.error("Error executing carousel data script:", error);
        }
      }
    });
  }

  static remountCarousels() {
    // Wait a bit for DOM to be fully updated
    setTimeout(() => {
      if (typeof window.remountCarousels === "function") {
        window.remountCarousels();
      }
    }, 100);
  }

  static reapplyGridViewPreference() {
    const savedPreference =
      localStorage.getItem("grid_view_preference") || "dual-column";
    const productGrid = document.getElementById("product-grid");

    if (productGrid) {
      // Remove existing grid classes
      productGrid.classList.remove("grid-cols-1", "grid-cols-2");

      // Add appropriate class based on saved preference
      if (savedPreference === "single-column") {
        productGrid.classList.add("grid-cols-1");
      } else {
        productGrid.classList.add("grid-cols-2");
      }
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, "text/html");
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      "#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter"
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      "#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter"
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (
        !Array.from(facetDetailsElementsFromFetch).some(
          ({ id }) => currentElement.id === id
        )
      ) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest(".js-filter") : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter(
      (element) => !matchesId(element)
    );
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(
      matchesId
    );

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML =
          elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } =
            facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document
            .querySelector(`#${elementToRender.parentElement.id} .js-filter`)
            .before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest(".js-filter").id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(
          countsToRender,
          event.target.closest(".js-filter")
        );
        FacetFiltersForm.renderMobileCounts(
          countsToRender,
          document.getElementById(closestJSFilterID)
        );

        const newFacetDetailsElement =
          document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains(
          "mobile-facets__details"
        )
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate =
          newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute("type") === "text";

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = [
      ".active-facets-mobile",
      ".active-facets-desktop",
      "#clear-button-container",
      "#product-count-container",
    ];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      const targetElement = document.querySelector(selector);
      if (!targetElement) return;
      targetElement.innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = [
      ".mobile-facets__open",
      ".mobile-facets__count",
      ".sorting",
    ];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML =
        html.querySelector(selector).innerHTML;
    });

    const mobileOpenButton = document.querySelectorAll(".filter-open-button");
    const screenWidth = window.innerWidth;

    document
      .getElementById("FacetFiltersFormMobile")
      .closest("menu-drawer")
      .bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector(".facets__summary");
    const sourceSummary = source.querySelector(".facets__summary");

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector(".facets__header");
    const sourceHeaderElement = source.querySelector(".facets__header");

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector(".facets-wrap");
    const sourceWrapElement = source.querySelector(".facets-wrap");

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(
        target.querySelector("show-more-button .label-show-more.hidden")
      );
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll(".facets__item.hidden")
          .forEach((hiddenItem) =>
            hiddenItem.classList.replace("hidden", "show-more-item")
          );
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector(".mobile-facets__list");
    const sourceFacetsList = source.querySelector(".mobile-facets__list");

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState(
      { searchParams },
      "",
      `${window.location.pathname}${searchParams && "?".concat(searchParams)}`
    );
  }

  static getSections() {
    return [
      {
        section: document.getElementById("product-grid").dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    const entries = [];
    formData.forEach((value, key) => {
      entries.push(`${key}=${value}`);
    });
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    console.log("Filters submitted");

    event.preventDefault();
    const sortFilterForms = document.querySelectorAll(
      "facet-filters-form form"
    );
    if (event.srcElement.className == "mobile-facets__checkbox") {
      const searchParams = this.createSearchParams(
        event.target.closest("form")
      );
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile =
        event.target.closest("form").id === "FacetFiltersFormMobile";

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (
            form.id === "FacetSortForm" ||
            form.id === "FacetFiltersForm" ||
            form.id === "FacetSortDrawerForm" ||
            form.id === "FacetSortingFormDesktop"
          ) {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === "FacetFiltersFormMobile") {
          forms.push(this.createSearchParams(form));
        }
      });

      let searchParams = forms.join("&");

      // Desktop sort lives in a sort-only form; preserve active filter.* from the URL
      if (!isMobile) {
        const params = new URLSearchParams(searchParams);
        const hasFilters = Array.from(params.keys()).some((key) =>
          key.startsWith("filter.")
        );
        if (!hasFilters) {
          const currentParams = new URLSearchParams(window.location.search);
          currentParams.forEach((value, key) => {
            if (key.startsWith("filter.")) {
              params.append(key, value);
            }
          });
          searchParams = params.toString();
        }
      }

      this.onSubmitForm(searchParams, event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf("?") == -1
        ? ""
        : event.currentTarget.href.slice(
            event.currentTarget.href.indexOf("?") + 1
          );
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define("facet-filters-form", FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll("input").forEach((element) => {
      element.addEventListener("change", this.onRangeChange.bind(this));
      element.addEventListener("keydown", this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern =
      /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll("input");
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute("data-max", maxInput.value);
    if (minInput.value) maxInput.setAttribute("data-min", minInput.value);
    if (minInput.value === "") maxInput.setAttribute("data-min", 0);
    if (maxInput.value === "")
      minInput.setAttribute("data-max", maxInput.getAttribute("data-max"));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute("data-min"));
    const max = Number(input.getAttribute("data-max"));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define("price-range", PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector("a");
    facetLink.setAttribute("role", "button");
    facetLink.addEventListener("click", this.closeFilter.bind(this));
    facetLink.addEventListener("keyup", (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === "SPACE") this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();

    const isClearAllButton = this.closest("#clear-button-container");

    if (isClearAllButton) {
      this.clearAllCheckboxes();

      // Start from current URL params and remove every filter.* entry
      const params = new URLSearchParams(window.location.search);
      Array.from(params.keys()).forEach((key) => {
        if (key.startsWith("filter.")) {
          params.delete(key);
        }
      });
      // Reset pagination when clearing filters
      params.delete("page");

      // Ensure sort_by matches the currently selected option (if any)
      const checkedSort = document.querySelector(
        'input[name="sort_by"]:checked'
      );
      if (checkedSort && checkedSort.value) {
        params.set("sort_by", checkedSort.value);
      } else if (!checkedSort) {
        params.delete("sort_by");
      }

      const searchParams = params.toString();
      FacetFiltersForm.renderPage(searchParams, event);
      return;
    }

    const form =
      this.closest("facet-filters-form") ||
      document.querySelector("facet-filters-form");
    form.onActiveFilterClick(event);
  }

  clearAllCheckboxes() {
    document
      .querySelectorAll('#FacetFiltersForm input[type="checkbox"]')
      .forEach((checkbox) => {
        checkbox.checked = false;
      });

    document
      .querySelectorAll('#FacetFiltersFormMobile input[type="checkbox"]')
      .forEach((checkbox) => {
        checkbox.checked = false;
      });

    document
      .querySelectorAll('#FacetFiltersPillsForm input[type="checkbox"]')
      .forEach((checkbox) => {
        checkbox.checked = false;
      });

    document
      .querySelectorAll(
        'input[name="filter.v.price.gte"], input[name="filter.v.price.lte"]'
      )
      .forEach((input) => {
        input.value = "";
      });
  }
}

customElements.define("facet-remove", FacetRemove);

class ViewSwitcher {
  constructor() {
    this.init();
  }

  init() {
    this.setupEventListeners();
    this.initializeView();
  }

  setupEventListeners() {
    document.addEventListener("change", (event) => {
      if (event.target.name === "sort_by_view") {
        this.updateGridView(event.target.value);
      }
    });
  }

  updateGridView(viewType) {
    const productGrid = document.getElementById("product-grid");
    if (!productGrid) return;

    productGrid.classList.remove("grid-cols-1", "grid-cols-2");

    if (viewType === "single-column") {
      productGrid.classList.add("grid-cols-1");
    } else {
      productGrid.classList.add("grid-cols-2");
    }
  }

  initializeView() {
    const checkedViewRadio = document.querySelector(
      'input[name="sort_by_view"]:checked'
    );
    if (checkedViewRadio) {
      this.updateGridView(checkedViewRadio.value);
    } else {
      this.updateGridView("dual-column");
    }
  }
}

document.addEventListener("DOMContentLoaded", () => {
  new ViewSwitcher();
});

// Ensure label clicks in mobile drawer toggle inputs (capture to bypass stopPropagation)
document.addEventListener(
  "click",
  (event) => {
    const label = event.target.closest(
      "label.mobile-facets__label, label.mobile-facets__sort-label"
    );
    if (!label) return;

    // Only apply within the mobile facets drawer
    if (!label.closest(".mobile-facets__inner")) return;

    // If user clicked directly on an input, let native behavior run
    if (event.target.closest("input")) return;

    const input = label.querySelector(
      'input[type="checkbox"], input[type="radio"]'
    );
    if (!input || input.disabled) return;

    // Prevent native label behavior to avoid double toggling
    event.preventDefault();

    // Toggle/check input explicitly
    if (input.type === "checkbox") {
      input.checked = !input.checked;
    } else if (input.type === "radio") {
      input.checked = true;
    }

    // Fire events so existing form listeners react
    input.dispatchEvent(new Event("input", { bubbles: true }));
    input.dispatchEvent(new Event("change", { bubbles: true }));
  },
  true
);

// Desktop-only delegated triggers survive DOM updates
document.addEventListener("click", (e) => {
  const isDesktop = window.innerWidth >= 750;

  const closeFiltersButton = e.target.closest("[data-close-filter-button]");
  if (closeFiltersButton) {
    e.preventDefault();

    const mobileDrawer = document.querySelector(
      "#filter-section .mobile-facets__wrapper"
    );

    if (mobileDrawer) {
      const summary = mobileDrawer.querySelector("summary");

      if (typeof mobileDrawer.closeMenuDrawer === "function" && summary) {
        mobileDrawer.closeMenuDrawer(new Event("click"), summary);
      } else if (summary) {
        summary.click();
      }
    } else if (typeof closeFacetsDrawer === "function") {
      closeFacetsDrawer();
    }

    return;
  }

  const filterTrigger = e.target.closest(".filter-open-trigger-desktop");
  if (filterTrigger && isDesktop) {
    e.preventDefault();
    if (typeof openFacetsDrawer === "function") openFacetsDrawer();
  }

  const sortTrigger = e.target.closest(".sort-open-trigger-desktop");
  if (sortTrigger && isDesktop) {
    e.preventDefault();
    if (typeof openSortingDrawer === "function") openSortingDrawer();
  }
});

function openFacetsDrawer() {
  const drawer = document.getElementById("Details-facets-drawer");
  if (!drawer) return;

  const isDesktop = window.innerWidth >= 750;

  // Store scroll position before preventing scroll
  window.facetsDrawerScrollPosition = window.pageYOffset;

  if (isDesktop) {
    // Force open on desktop (skip mobile menu-drawer API)
    drawer.setAttribute("open", "");
    drawer.classList.add("menu-opening");

    // Prevent body scrolling with position: fixed
    document.body.style.position = "fixed";
    document.body.style.top = `-${window.facetsDrawerScrollPosition}px`;
    document.body.style.width = "100%";
    document.body.classList.add("overflow-hidden-desktop");

    const summary = drawer.querySelector("summary");
    if (summary) summary.setAttribute("aria-expanded", "true");

    const focusable = drawer.querySelector(
      "button, input, select, textarea, a[href]"
    );
    if (focusable) focusable.focus();
    return;
  }

  // Mobile behavior - let menu-drawer handle it, but we still store scroll position
  const menuDrawer = drawer.closest(".mobile-facets__wrapper");
  const summary = drawer.querySelector("summary");
  if (
    menuDrawer &&
    summary &&
    typeof menuDrawer.openMenuDrawer === "function"
  ) {
    menuDrawer.openMenuDrawer(summary);
  } else {
    drawer.setAttribute("open", "");
    drawer.classList.add("menu-opening");

    // Prevent body scrolling with position: fixed
    document.body.style.position = "fixed";
    document.body.style.top = `-${window.facetsDrawerScrollPosition}px`;
    document.body.style.width = "100%";
    document.body.classList.add("overflow-hidden");

    if (summary) summary.setAttribute("aria-expanded", "true");
  }
}

function closeFacetsDrawer() {
  const drawer = document.getElementById("Details-facets-drawer");
  if (drawer) {
    drawer.classList.remove("menu-opening");

    // Remove overflow classes
    document.body.classList.remove("overflow-hidden-desktop");
    document.body.classList.remove("overflow-hidden");

    // Restore body styles and scroll position
    const scrollPosition = window.facetsDrawerScrollPosition || 0;
    document.body.style.position = "";
    document.body.style.top = "";
    document.body.style.width = "";
    window.scrollTo(0, scrollPosition);

    // Clear stored scroll position
    window.facetsDrawerScrollPosition = null;

    // Prefer native menu-drawer close if available
    const menu = drawer.closest("menu-drawer");
    const summary = drawer.querySelector("summary");
    if (menu && typeof menu.closeMenuDrawer === "function") {
      menu.closeMenuDrawer(new Event("click"), summary || drawer);
      return;
    }

    setTimeout(() => {
      drawer.removeAttribute("open");
      if (summary) {
        summary.setAttribute("aria-expanded", "false");
      }
    }, 300);
  }
}

// Add sorting drawer functions
function openSortingDrawer() {
  const drawer = document.getElementById("Details-sorting-drawer");
  if (drawer) {
    // Store scroll position before preventing scroll
    window.sortingDrawerScrollPosition = window.pageYOffset;

    drawer.setAttribute("open", "");
    drawer.classList.add("menu-opening");

    // Prevent body scrolling with position: fixed (desktop drawer)
    document.body.style.position = "fixed";
    document.body.style.top = `-${window.sortingDrawerScrollPosition}px`;
    document.body.style.width = "100%";
    document.body.classList.add("overflow-hidden-desktop");

    const summary = drawer.querySelector("summary");
    if (summary) {
      summary.setAttribute("aria-expanded", "true");
    }
  }
}

function closeSortingDrawer() {
  const drawer = document.getElementById("Details-sorting-drawer");
  if (drawer) {
    drawer.classList.remove("menu-opening");

    // Remove overflow classes
    document.body.classList.remove("overflow-hidden-desktop");
    document.body.classList.remove("overflow-hidden");

    // Restore body styles and scroll position
    const scrollPosition = window.sortingDrawerScrollPosition || 0;
    document.body.style.position = "";
    document.body.style.top = "";
    document.body.style.width = "";
    window.scrollTo(0, scrollPosition);

    // Clear stored scroll position
    window.sortingDrawerScrollPosition = null;

    // Prefer native menu-drawer close if available
    const menu = drawer.closest("menu-drawer");
    const summary = drawer.querySelector("summary");
    if (menu && typeof menu.closeMenuDrawer === "function") {
      menu.closeMenuDrawer(new Event("click"), summary || drawer);
      return;
    }

    setTimeout(() => {
      drawer.removeAttribute("open");
      if (summary) {
        summary.setAttribute("aria-expanded", "false");
      }
    }, 300);
  }
}

// Watch for when drawers close (via close buttons or other means) and restore scroll
document.addEventListener("DOMContentLoaded", () => {
  const facetsDrawer = document.getElementById("Details-facets-drawer");
  const sortingDrawer = document.getElementById("Details-sorting-drawer");

  // Listen for toggle events on the drawers
  if (facetsDrawer) {
    facetsDrawer.addEventListener("toggle", (event) => {
      // When drawer closes (open attribute removed)
      if (
        !event.target.open &&
        window.facetsDrawerScrollPosition !== null &&
        window.facetsDrawerScrollPosition !== undefined
      ) {
        // Restore body styles and scroll position
        document.body.style.position = "";
        document.body.style.top = "";
        document.body.style.width = "";
        document.body.classList.remove("overflow-hidden-desktop");
        document.body.classList.remove("overflow-hidden");

        const scrollPosition = window.facetsDrawerScrollPosition || 0;
        window.scrollTo(0, scrollPosition);
        window.facetsDrawerScrollPosition = null;
      }
    });
  }

  if (sortingDrawer) {
    sortingDrawer.addEventListener("toggle", (event) => {
      // When drawer closes (open attribute removed)
      if (
        !event.target.open &&
        window.sortingDrawerScrollPosition !== null &&
        window.sortingDrawerScrollPosition !== undefined
      ) {
        // Restore body styles and scroll position
        document.body.style.position = "";
        document.body.style.top = "";
        document.body.style.width = "";
        document.body.classList.remove("overflow-hidden-desktop");
        document.body.classList.remove("overflow-hidden");

        const scrollPosition = window.sortingDrawerScrollPosition || 0;
        window.scrollTo(0, scrollPosition);
        window.sortingDrawerScrollPosition = null;
      }
    });
  }
});
